import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Icon, ProgressBar } from '../../ui';
import { Categoria } from '../../servicos/categorias/typeCategoria';
import { Transacao } from '../../servicos/transacoes/typeTransacao';
import { hexToRgba, valorCurto } from './uteis';
import styles from './MetasCard.module.css';

type MetasCardProps = {
  transacoes: Transacao[];
  categorias: Categoria[];
};

type ResumoMeta = {
  categoria: Categoria;
  realizado: number;
  percentual: number;
};

/**
 * Resumo das metas: top 4 categorias com meta definida, ordenadas pelas mais
 * consumidas, com barra de progresso e link para a tela de metas.
 */
export default function MetasCard({ transacoes, categorias }: MetasCardProps) {
  const navigate = useNavigate();

  const metas = useMemo<ResumoMeta[]>(() => {
    return categorias
      .filter((categoria) => (categoria.valorMeta ?? 0) > 0)
      .map((categoria) => {
        const realizado = transacoes
          .filter((transacao) => transacao.categoriaId === categoria.id && transacao.tipo === categoria.tipo)
          .reduce((soma, transacao) => soma + Number(transacao.valor), 0);
        return {
          categoria,
          realizado,
          percentual: (realizado / (categoria.valorMeta as number)) * 100
        };
      })
      .sort((a, b) => b.percentual - a.percentual)
      .slice(0, 4);
  }, [categorias, transacoes]);

  return (
    <Card className={styles.cartao}>
      <div className={styles.cabecalho}>
        <h2 className={styles.titulo}>Metas do mês</h2>
        <button type="button" className={styles.link} onClick={() => navigate('/metas')}>
          Ver todas →
        </button>
      </div>

      {metas.length === 0 ? (
        <div className={styles.vazio}>Defina metas mensais nas suas categorias para acompanhá-las por aqui.</div>
      ) : (
        <div className={styles.lista}>
          {metas.map(({ categoria, realizado, percentual }) => {
            const cor = categoria.corIdentificacao?.trim() || '';
            return (
              <div key={categoria.id} className={styles.item}>
                <div className={styles.itemTopo}>
                  <span
                    className={styles.iconeCategoria}
                    style={{
                      color: cor || 'var(--textSecondary)',
                      background: cor ? hexToRgba(cor, 0.12) : 'var(--medium-gray)'
                    }}
                  >
                    <Icon nome={categoria.icone || 'category'} tamanho={16} />
                  </span>
                  <span className={styles.nome}>{categoria.nome}</span>
                  <span className={styles.valores}>
                    {valorCurto(realizado)} <span className={styles.meta}>de {valorCurto(categoria.valorMeta ?? 0)}</span>
                  </span>
                </div>
                <ProgressBar percentual={percentual} tom={categoria.tipo === 'R' ? 'auto-receita' : 'auto-despesa'} />
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}
